import { Schema } from "../../Schema";
import { getAndProcessLinkedInstances } from "../common/external-sources/LinkedInstances";
import { getAndProcessSites } from "../common/external-sources/Sites";
import { getAndProcessTags } from "../common/external-sources/Tags";
import { getAndProcessLabAreas } from "../common/external-sources/LabAreas";
import { getAndProcessSops } from "./external-sources/Sops";
import { getAndProcessReasons } from "../common/external-sources/Reasons";
import { getCategoryMappings } from "../common/external-sources/CategoryMappings";
import { is4epMode } from "../common/fields/is4epMode";
import { SchemaClient } from "../../common/GraphqlClient";
import {
  entryType,
  originalValues,
  id,
  createdBy,
  updatedBy,
  mode,
  status,
  secondaryID,
  secondaryIDMapping,
  siteId,
  siteTimezone,
  location,
  createLinkedInstance,
  createSiteName,
  serialNumber,
  equipmentId,
  materialNumber,
  inventoryNumber,
  instrumentGTIN,
  masterDataPicker,
  createEquipmentCategory,
  createManufacturer,
  createEquipmentModel,
  createVariant,
  equipmentNickName,
  network,
  createBuildingLocation,
  createFloor,
  createRoom,
  createLabArea,
  createTags,
  createSop,
  electronicRecord,
  electronicSignatures,
  dateOfNextPeriodicReview,
  qualificationStatus,
  controlledEquipmentStatus,
  documentedMaintenanceDate,
  maintenanceIntervalInDays,
  qualificationDocuments,
  softwareVersion,
  configurationBaseline,
  installedTests,
  isBookable,
  isVisualized,
  checkInRequired,
  checkInStartPeriod,
  checkInGracePeriod,
  isHazardous,
  shouldAssisted,
  createEditReason,
  editComment,
  createReviewers,
  cluster,
  comment,
  maintenancePlan,
  instrumentRUDI,
  functionalLocation,
  costCenter,
  equipmentAdministrator,
  systemOwner,
  responsibleProxy,
  createResponsiblePerson,
  createBelongingToGroup,
  createContactPerson,
  description,
  configurationDetails,
  csv,
  gxpRelevant,
  systemStatus,
  dateOfLastCalibration,
  dateOfLastMaintanance,
  dateOfNextMaintanance,
  currentUser,
  equipmentType,
  portability,
  vnvStatus,
  vnvMaturityClassification,
  createMaintenancePerformedBy,
  createdAt,
  updatedAt,
  weightInkg,
  heightInmm,
  lengthInmm,
  depthInmm,
  clearanceLeftInmm,
  clearanceRightInmm,
  clearanceHeightInmm,
  clearanceToFrontInmm,
  clearanceBackwardsInmm,
  heatOutputInW,
  noiseIndBA,
  noiseSource,
  noiseSourceDetails,
  pressureEquipment,
  positioning,
  positioningNote,
  vibrationSensitive,
  displayImage,
  modelName,
  clusterId,
  positionInCluster,
  trackPositionInCluster,
  isClusterSWVersion,
  reviewAction,
  reviewId,
} from "./fields";
import { EquipmentContext } from "../../../schema";

// Re-export types for backwards compatibility
export * from ".";

/**
 * The master-data sources the equipment schema resolves its enum options from.
 *
 * Each arrives as a plain list captured when the schema is built, so a source that could
 * not answer leaves an empty list — and `DataManager` then reduces every value for the
 * fields that depend on it to `EMPTY_VALUE`. A read path can live with that, because the
 * CSV translators fall back to the stored value. A write path cannot: it would null the
 * affected fields on every record and report success.
 *
 * {@link findEmptyEquipmentSources} names the ones that came back empty so a caller that
 * writes can refuse to run.
 */
export interface EquipmentSources {
  linkedInstances: unknown[];
  sites: unknown[];
  tags: unknown[];
  labAreas: unknown[];
  sops: unknown[];
  reasons: unknown[];
  categoryMappings: unknown[];
}

/**
 * Fields whose options come from each source, for an error a reader can act on.
 *
 * `categoryMappings` is deliberately absent. Every source listed here backs a `data` aspect,
 * where an empty list makes `DataManager` null the field on every record written — that is
 * what makes it worth refusing to run over. The mappings back a grid filter and one validation
 * rule, neither of which writes anything, so an unreadable table costs the tuple check for
 * that run and nothing else. The rule treats an empty list as "unavailable" and stays quiet.
 */
type AssertedSource = Exclude<keyof EquipmentSources, "categoryMappings">;

const SOURCE_FIELDS: Record<AssertedSource, string[]> = {
  linkedInstances: ["linkedInstance"],
  sites: ["siteName", "buildingLocation", "floor", "room"],
  tags: ["tags"],
  labAreas: ["labArea"],
  sops: ["sop"],
  reasons: ["editReason"],
};

/**
 * Names the master-data sources that returned nothing, with the fields each one feeds.
 *
 * @param sources - The sources a schema was built from.
 * @returns One entry per empty source; empty when every source answered.
 */
export const findEmptyEquipmentSources = (
  sources: EquipmentSources
): { source: string; fields: string[] }[] =>
  (Object.keys(SOURCE_FIELDS) as AssertedSource[])
    .filter((source) => !sources[source]?.length)
    .map((source) => ({ source, fields: SOURCE_FIELDS[source] }));

/**
 * Refuses to continue when a master-data source came back empty.
 *
 * The check every writer needs and none of them should spell out for itself. An empty option
 * list makes `DataManager` reduce every value of the fields reading it to `EMPTY_VALUE`, so a
 * table the caller cannot reach turns a write into silent data loss across every record it
 * touches. Failing means the operation is reported as failed and can be retried, which is the
 * only safe outcome available.
 *
 * @param sources - The sources a schema was built from.
 * @param action - What is being refused, for the message: "edit", "import", ...
 * @throws {Error} When a source is unavailable, naming the sources and the fields they feed.
 */
export const assertEquipmentSourcesAvailable = (
  sources: EquipmentSources,
  action: string
): void => {
  const empty = findEmptyEquipmentSources(sources);
  if (!empty.length) {
    return;
  }
  const detail = empty
    .map(({ source, fields }) => `${source} (${fields.join(", ")})`)
    .join("; ");
  throw new Error(
    `Master data unavailable, refusing to ${action}: ${detail}. ` +
      "Continuing would clear these fields on every record written."
  );
};

/**
 * Builds the equipment schema and hands back the sources it was built from, so a caller
 * that writes can check them before trusting the enum coercion.
 *
 * @param client - Read client for the master data.
 * @returns The schema and the sources behind it.
 */
export const prepareEquipmentSchemaWithSources = async (
  client: SchemaClient
): Promise<{ schema: Schema; sources: EquipmentSources }> => {
  const [linkedInstances, sites, tags, labAreas, sops, reasons, categoryMappings] =
    await Promise.all([
      getAndProcessLinkedInstances(client),
      getAndProcessSites(client),
      getAndProcessTags(client),
      getAndProcessLabAreas(client),
      getAndProcessSops(client),
      getAndProcessReasons(client),
      getCategoryMappings(client),
    ]);

  const context: EquipmentContext = {
    client, linkedInstances, sites, tags, labAreas, sops, reasons, categoryMappings,
  };
  const sources: EquipmentSources = {
    linkedInstances, sites, tags, labAreas, sops, reasons, categoryMappings,
  };

  const schema: Schema = {
    currentUser,
    entryType,
    modelName,
    displayImage,
    clusterId,
    positionInCluster,
    trackPositionInCluster,
    isClusterSWVersion,
    reviewAction,
    reviewId,
    is4epMode,
    originalValues,
    id,
    createdBy,
    updatedBy,
    mode,
    status,
    secondaryID,
    secondaryIDMapping,
    siteId,
    siteTimezone,
    location,
    linkedInstance: createLinkedInstance(context),
    siteName: createSiteName(context),
    serialNumber,
    equipmentId,
    materialNumber,
    inventoryNumber,
    instrumentRUDI,
    instrumentGTIN,
    masterDataPicker,
    equipmentCategory: createEquipmentCategory(context),
    manufacturer: createManufacturer(context),
    equipmentModel: createEquipmentModel(context),
    variant: createVariant(context),
    equipmentType,
    description,
    cluster,
    tags: createTags(context),
    equipmentNickName,
    comment,
    network,
    belongingToGroup: createBelongingToGroup(context),
    buildingLocation: createBuildingLocation(context),
    floor: createFloor(context),
    room: createRoom(context),
    labArea: createLabArea(context),
    portability,
    vnvStatus,
    vnvMaturityClassification,
    responsiblePerson: createResponsiblePerson(context),
    equipmentAdministrator,
    systemOwner,
    responsibleProxy,
    contactPerson: createContactPerson(context),
    functionalLocation,
    costCenter,
    sop: createSop(context),
    csv,
    electronicRecord,
    electronicSignatures,
    dateOfNextPeriodicReview,
    maintenancePlan,
    gxpRelevant,
    qualificationStatus,
    controlledEquipmentStatus,
    dateOfLastMaintanance,
    maintenancePerformedBy: createMaintenancePerformedBy(context),
    dateOfLastCalibration,
    maintenanceIntervalInDays,
    dateOfNextMaintanance,
    documentedMaintenanceDate,
    qualificationDocuments,
    systemStatus,
    softwareVersion,
    configurationBaseline,
    installedTests,
    configurationDetails,
    isBookable,
    isVisualized,
    checkInRequired,
    checkInStartPeriod,
    checkInGracePeriod,
    isHazardous,
    shouldAssisted,
    editReason: createEditReason(context),
    editComment,
    reviewers: createReviewers(context),
    createdAt,
    updatedAt,
    weightInkg,
    heightInmm,
    lengthInmm,
    depthInmm,
    clearanceLeftInmm,
    clearanceRightInmm,
    clearanceHeightInmm,
    clearanceToFrontInmm,
    clearanceBackwardsInmm,
    heatOutputInW,
    noiseIndBA,
    noiseSource,
    noiseSourceDetails,
    pressureEquipment,
    positioning,
    positioningNote,
    vibrationSensitive,
  };

  return { schema, sources };
};

/**
 * Builds the equipment schema.
 *
 * A caller that writes equipment should prefer {@link prepareEquipmentSchemaWithSources},
 * so it can tell an unreachable master-data source from a genuinely empty one.
 *
 * @param client - Read client for the master data.
 * @returns The schema.
 */
export const prepareEquipmentSchema = async (client: SchemaClient): Promise<Schema> =>
  (await prepareEquipmentSchemaWithSources(client)).schema;
